import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InputErrorMessage,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@commenda/kakashi';
import { useState } from 'react';

import {
  CommendaAiFeedbackRating,
  type CommendaAiFeedbackCategory,
  type CommendaAiMessageFeedbackDetails,
} from '@commenda/api/commenda-ai/types';

import { CommendaAiMessageFeedbackHelper } from './message-feedback-helper';

interface MessageFeedbackModalProps {
  /** The rating being composed. Null closes the modal. */
  rating: CommendaAiFeedbackRating | null;
  /** The verdict already saved for this rating, so a reopened modal shows what was submitted.
   * Undefined opens the form fresh. */
  initialDetails?: CommendaAiMessageFeedbackDetails;
  isSubmitting: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (details: Readonly<CommendaAiMessageFeedbackDetails>) => void;
}

/** Nothing is saved until Submit; Cancel discards the rating. Each polarity has its own category
 * list, thumbs-up preselecting GOOD_RESPONSE. Mount with a `key` per open so this state resets. */
export function MessageFeedbackModal({
  rating,
  initialDetails,
  isSubmitting,
  error,
  onCancel,
  onSubmit,
}: MessageFeedbackModalProps) {
  const isPositive = rating === CommendaAiFeedbackRating.UP;

  const [category, setCategory] = useState<
    CommendaAiFeedbackCategory | undefined
  >(
    initialDetails?.category ??
      (isPositive
        ? CommendaAiMessageFeedbackHelper.DEFAULT_POSITIVE_CATEGORY
        : undefined)
  );
  const [comment, setComment] = useState(initialDetails?.comment ?? '');

  const handleSubmit = () => {
    onSubmit({
      category,
      comment: comment.trim() || undefined,
    });
  };

  return (
    <Dialog open={rating !== null} onOpenChange={onCancel}>
      <DialogContent className="h-fit w-[520px] max-w-[95vw]">
        <DialogHeader>
          <DialogTitle>
            {isPositive ? 'Give positive feedback' : 'Give feedback'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2xl p-3xl">
          <div className="flex flex-col gap-sm">
            <Label>
              {isPositive
                ? 'What did you like? (optional)'
                : 'What type of issue? (optional)'}
            </Label>
            <Select
              value={category}
              onValueChange={(value) =>
                // Safe: the Select only emits values from getFeedbackCategoryOptions, which are
                // typed CommendaAiFeedbackCategory.
                setCategory(value as CommendaAiFeedbackCategory)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {rating &&
                  CommendaAiMessageFeedbackHelper.getFeedbackCategoryOptions(
                    rating
                  ).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-sm">
            <Label>Please provide details: (optional)</Label>
            <Textarea
              rows={4}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={
                isPositive
                  ? 'What was satisfying about this response?'
                  : 'What went wrong?'
              }
              maxLength={2000}
            />
          </div>
          {error && <InputErrorMessage message={error} />}
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
